import { DatePicker, DropdownSelect } from '@components/common';
import { useBookings, useBookingSources } from '@hooks/accommodation';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  formatDateTime,
  guestLabel,
  nightsBetween,
  roomLabel,
  STATUS_FILTERS,
} from '@lib/utils';
import {
  selectAccommodationProperty,
  setSelectedBooking,
} from '@store/accommodation';
import { Download, Filter, Search } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { exportReportCsv } from '../../../../services/reportsService';
import {
  AccommodationBooking,
  AccommodationBookingStatus,
} from '../../../../types/accommodation';
import { apiError, apiSuccess } from '../../../../util/notify';
import { BookingsTable } from './BookingsTable';

const PAGE_SIZE = 15;

export const BookingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const property = useAppSelector(selectAccommodationProperty);
  const { data: bookingSources = [] } = useBookingSources();

  const [statusFilter, setStatusFilter] = useState<
    AccommodationBookingStatus | 'all'
  >('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const bookingFilters = {
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(dateFrom ? { check_in_from: dateFrom } : {}),
    ...(dateTo ? { check_in_to: dateTo } : {}),
  };

  const { data: bookings = [], isLoading } = useBookings(
    property?.id,
    Object.keys(bookingFilters).length > 0 ? bookingFilters : undefined,
  );

  const sourceRef = useRef<HTMLDivElement>(null);

  // Close source dropdown on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (sourceRef.current && !sourceRef.current.contains(e.target as Node))
        setShowSourceMenu(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, sourceFilter, search, dateFrom, dateTo]);

  const filtered = useMemo(
    () =>
      bookings.filter((b) => {
        if (search.trim()) {
          const q = search.toLowerCase();
          const name = b.client
            ? `${b.client.first_name} ${b.client.last_name}`.toLowerCase()
            : '';
          const email = (b.client?.email ?? '').toLowerCase();
          if (!name.includes(q) && !email.includes(q)) return false;
        }
        if (sourceFilter !== 'all') {
          if (String(b.booking_source?.id ?? '') !== sourceFilter) return false;
        }
        return true;
      }),
    [bookings, search, sourceFilter],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Select-all for current page
  const allPageSelected =
    paginated.length > 0 && paginated.every((b) => selected.has(b.id));

  const toggleAll = () => {
    if (allPageSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        paginated.forEach((b) => next.delete(b.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        paginated.forEach((b) => next.add(b.id));
        return next;
      });
    }
  };

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRowClick = (e: React.MouseEvent, b: AccommodationBooking) => {
    // Don't open preview if clicking the checkbox cell or action button
    const target = e.target as HTMLElement;
    if (target.closest('[data-no-preview]')) return;
    dispatch(setSelectedBooking(b));
  };

  const handleExport = async () => {
    if (filtered.length === 0) return;
    setIsExporting(true);
    try {
      const columns = [
        { key: 'ref', header: 'Booking Ref' },
        { key: 'guest', header: 'Guest' },
        { key: 'email', header: 'Email' },
        { key: 'rooms', header: 'Type & Room' },
        { key: 'check_in', header: 'Check-in' },
        { key: 'check_out', header: 'Check-out' },
        { key: 'nights', header: 'Nights' },
        { key: 'source', header: 'Source' },
        { key: 'total_amount', header: 'Total Amount' },
        { key: 'amount_paid', header: 'Paid Amount' },
        { key: 'balance_due', header: 'Due Amount' },
        { key: 'status', header: 'Status' },
      ];
      const formatBookingAmount = (value: number) =>
        value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      const rows = filtered.map((b) => {
        const amountPaid = b.amount_paid ?? 0;
        return {
          ref: `#${b.id.slice(0, 8).toUpperCase()}`,
          guest: guestLabel({
            firstName: b.client?.first_name,
            lastName: b.client?.last_name,
          }),
          email: b.client?.email ?? '',
          rooms: roomLabel(b),
          check_in: formatDateTime(
            b.check_in,
            property?.check_in_time ?? '14:00',
          ),
          check_out: formatDateTime(
            b.check_out,
            property?.check_out_time ?? '11:00',
          ),
          nights: nightsBetween(b.check_in, b.check_out),
          source: b.booking_source?.name ?? '',
          total_amount: formatBookingAmount(b.total_amount),
          amount_paid: formatBookingAmount(amountPaid),
          balance_due: formatBookingAmount(
            Math.max(0, b.total_amount - amountPaid),
          ),
          status: b.status,
        };
      });
      await exportReportCsv({
        title: 'Accommodation Bookings',
        fileName: `bookings-${new Date().toISOString().slice(0, 10)}.csv`,
        columns,
        rows,
      });
      apiSuccess('Exported successfully.');
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  // Page numbers to show — use objects so ellipsis entries have stable keys
  const pageNums = useMemo(() => {
    type PageEntry = { key: string; val: number | '…' };
    const e = (v: number | '…', k: string): PageEntry => ({ key: k, val: v });
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) =>
        e(i + 1, String(i + 1)),
      );
    if (page <= 4)
      return [
        e(1, '1'),
        e(2, '2'),
        e(3, '3'),
        e(4, '4'),
        e(5, '5'),
        e('…', 'el1'),
        e(totalPages, 'last'),
      ];
    if (page >= totalPages - 3)
      return [
        e(1, '1'),
        e('…', 'el1'),
        e(totalPages - 4, String(totalPages - 4)),
        e(totalPages - 3, String(totalPages - 3)),
        e(totalPages - 2, String(totalPages - 2)),
        e(totalPages - 1, String(totalPages - 1)),
        e(totalPages, 'last'),
      ];
    return [
      e(1, '1'),
      e('…', 'el1'),
      e(page - 1, String(page - 1)),
      e(page, String(page)),
      e(page + 1, String(page + 1)),
      e('…', 'el2'),
      e(totalPages, 'last'),
    ];
  }, [page, totalPages]);

  return (
    <div className="space-y-3">
      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Left: filter dropdowns */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Status pill dropdown */}
          <DropdownSelect
            value={statusFilter}
            onChange={(v) =>
              setStatusFilter(v as AccommodationBookingStatus | 'all')
            }
            options={STATUS_FILTERS.map((f) => ({
              value: f.value,
              label: f.label,
            }))}
          />

          {/* Source filter */}
          <div className="relative" ref={sourceRef}>
            <button
              type="button"
              onClick={() => setShowSourceMenu((v) => !v)}
              className={[
                'flex h-8 items-center gap-1.5 rounded-xl border px-3 text-xs font-medium transition-colors',
                sourceFilter !== 'all'
                  ? 'border-pos-primary bg-pos-primary/10 text-pos-primary'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
              ].join(' ')}
            >
              <Filter size={11} />
              {sourceFilter === 'all'
                ? 'All Group'
                : (bookingSources.find((s) => String(s.id) === sourceFilter)
                    ?.name ?? 'Source')}
            </button>
            {showSourceMenu && (
              <div className="absolute left-0 top-full z-20 mt-1 min-w-[160px] rounded-xl border border-gray-200 bg-white shadow-lg">
                {[
                  { id: 'all', name: 'All Groups' },
                  ...bookingSources.map((s) => ({
                    id: String(s.id),
                    name: s.name,
                  })),
                ].map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setSourceFilter(s.id);
                      setShowSourceMenu(false);
                    }}
                    className={[
                      'block w-full px-3 py-2 text-left text-xs transition-colors first:rounded-t-xl last:rounded-b-xl',
                      sourceFilter === s.id
                        ? 'bg-pos-primary/10 font-medium text-pos-primary'
                        : 'text-gray-700 hover:bg-gray-50',
                    ].join(' ')}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Room type filter — placeholder */}
          <button
            type="button"
            className="flex h-8 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Type &amp; Room
          </button>

          {/* Status quick-filter */}
          <button
            type="button"
            className="flex h-8 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Status
          </button>

          {/* Check-in date range filter */}
          <DatePicker
            mode="range"
            startDate={dateFrom}
            endDate={dateTo}
            onRangeChange={(s, e) => {
              setDateFrom(s || null);
              setDateTo(e || null);
            }}
            placeholder="Check-in range"
            label=""
            id="bookings-checkin-range"
          />
        </div>

        {/* Right: search + export */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="h-8 w-44 rounded-xl border border-gray-200 pl-8 pr-3 text-xs focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
            />
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting || filtered.length === 0}
            className="flex h-8 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download size={12} />
            Export CSV
          </button>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-200 bg-white">
        {isLoading && filtered.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-gray-400">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-gray-400">
            No bookings found
          </div>
        ) : (
          <>
            {/* Table */}
            <BookingsTable
              bookings={paginated}
              allPageSelected={allPageSelected}
              selected={selected}
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
              onRowClick={handleRowClick}
            />

            {/* ── Pagination ─────────────────────────────────── */}
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
              <p className="text-xs text-gray-400">
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–
                {Math.min(page * PAGE_SIZE, filtered.length)} of{' '}
                {filtered.length} bookings
                {selected.size > 0 && (
                  <span className="ml-2 text-pos-primary">
                    · {selected.size} selected
                  </span>
                )}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="flex h-7 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  ← Previous
                </button>
                {pageNums.map(({ key, val }) =>
                  val === '…' ? (
                    <span key={key} className="px-1 text-xs text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setPage(val as number)}
                      className={[
                        'flex h-7 w-7 items-center justify-center rounded-lg border text-xs',
                        page === val
                          ? 'border-pos-primary bg-pos-primary font-semibold text-white'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {val}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="flex h-7 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
